// VFX 모듈 13종을 묶는 곳(D1d 1차 6종 + 2차 6종 + c017 3차 유도 줄기). 렌더가 한 번 만들어 장면에 붙이고, Ev.SkillCue(vfx 채널)를 onCue로 넘기고, 프레임마다 update한다.
// 모듈마다 드로우콜 1회(총 13 이하), 동시 활성 상한은 MODULE_CAPS. 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다
// (지연 큐·파라미터 객체도 생성 때 고정 크기로 만들어 재사용한다).
import * as THREE from 'three/webgpu';
import { ArcModule } from './arc';
import { AuraModule } from './aura';
import { BeamModule } from './beam';
import { BlastModule, createBlastTexture } from './blast';
import { CrackModule } from './crack';
import { CUE_MAP, cueNameOf, cueOccurrenceOf, findCue, type CueEntry } from './cueMap';
import { createNoiseTexture } from './noise';
import { ProjectileModule } from './projectile';
import { PillarModule } from './pillar';
import { RingModule } from './ring';
import { ScreenModule } from './screen';
import { StreakModule } from './streak';
import { TrailModule } from './trail';
import { fillParams, makeParams, TICK_HZ, type FxModule, type FxPreset, type FxTarget, type ModuleKind } from './types';
import { VortexModule } from './vortex';
import { ZoneModule } from './zone';

export * from './types';
export { ArcModule, AuraModule, BeamModule, BlastModule, CrackModule, PillarModule, ProjectileModule, RingModule, ScreenModule, StreakModule, TrailModule, VortexModule, ZoneModule };
export { BLAST_FRAMES, BLAST_GRID, blastFrame, blastCell, buildBlastSheetData } from './blastSheet';
export { AV_ONLY_CUES, CUE_MAP, cueNameOf, cueOccurrenceOf, findCue, timelineVfxCues, type CueEntry, type CueLayer } from './cueMap';

/** 지연 큐 칸 수(한 큐가 만드는 지연 층 수 × 동시 큐) */
const PENDING_CAP = 24;

class Pending {
  t = 0;
  module: FxModule | null = null;
  preset: FxPreset | null = null;
  x = 0; y = 0; z = 0; yaw = 0;
}

export class VfxModules {
  readonly group = new THREE.Group();
  readonly arc: ArcModule;
  readonly trail: TrailModule;
  readonly ring: RingModule;
  readonly crack: CrackModule;
  readonly projectile: ProjectileModule;
  readonly screen: ScreenModule;
  readonly beam: BeamModule;
  readonly zone: ZoneModule;
  readonly pillar: PillarModule;
  readonly blast: BlastModule;
  readonly vortex: VortexModule;
  readonly aura: AuraModule;
  /** 3차(c017): 장비 줍기·고유 효과 유도 줄기 */
  readonly streak: StreakModule;
  /** 생성 순서 고정 배열(순회용) */
  readonly all: readonly FxModule[];
  private readonly byKind: Record<ModuleKind, FxModule>;
  private readonly noise: THREE.DataTexture;
  private readonly blastSheet: THREE.DataTexture;
  private readonly params = makeParams();
  private readonly pending: Pending[] = [];
  private pendingN = 0;
  /** 받은 큐 수 / 새 모듈이 그린 큐 수 / 기록뿐이라 건너뛴 큐 수 / 이름 없는 큐 수(QA) */
  cues = { seen: 0, drawn: 0, skipped: 0, unknown: 0 };

  constructor() {
    this.group.name = 'vfx-modules';
    this.noise = createNoiseTexture();
    this.arc = new ArcModule(this.noise);
    this.trail = new TrailModule(this.noise);
    this.ring = new RingModule(this.noise);
    this.crack = new CrackModule(this.noise);
    this.projectile = new ProjectileModule(this.noise);
    this.screen = new ScreenModule();
    this.blastSheet = createBlastTexture();
    this.beam = new BeamModule(this.noise);
    this.zone = new ZoneModule(this.noise);
    this.pillar = new PillarModule(this.noise);
    this.blast = new BlastModule(this.blastSheet);
    this.vortex = new VortexModule(this.noise);
    this.aura = new AuraModule(this.noise);
    this.streak = new StreakModule(this.noise);
    this.all = [
      this.arc, this.trail, this.ring, this.crack, this.projectile, this.screen,
      this.beam, this.zone, this.pillar, this.blast, this.vortex, this.aura,
      this.streak,
    ];
    this.byKind = {
      arc: this.arc, trail: this.trail, ring: this.ring, crack: this.crack, projectile: this.projectile, screen: this.screen,
      beam: this.beam, zone: this.zone, pillar: this.pillar, blast: this.blast, vortex: this.vortex, aura: this.aura,
      streak: this.streak,
    };
    for (const m of this.all) this.group.add(m.group);
    for (let i = 0; i < PENDING_CAP; i++) this.pending.push(new Pending());
  }

  /** 지금 살아 있는 모든 모듈 인스턴스 수 */
  get activeCount(): number {
    let n = 0;
    for (const m of this.all) n += m.activeCount;
    return n;
  }

  /** 지금 이 라이브러리가 쓰는 드로우콜(모듈마다 0 또는 1) */
  get drawCalls(): number {
    let n = 0;
    for (const m of this.all) n += m.drawCalls;
    return n;
  }

  module(kind: ModuleKind): FxModule { return this.byKind[kind]; }

  /** 섬광 약화(게임 설정): 화면 효과 세기 40% + 화면 효과 외 모든 모듈 밝기 70% */
  setReduceFlash(on: boolean): void {
    this.screen.setReduceFlash(on);
    const g = on ? 0.7 : 1;
    this.arc.setGain(g); this.trail.setGain(g); this.ring.setGain(g); this.crack.setGain(g); this.projectile.setGain(g);
    this.beam.setGain(g); this.zone.setGain(g); this.pillar.setGain(g); this.blast.setGain(g); this.vortex.setGain(g); this.aura.setGain(g);
    this.streak.setGain(g);
  }

  /** 카메라 시선 방향(월드): 카메라를 향해 펼치는 트레일(variant 2)·오라 가장자리 흐림이 쓴다. 렌더가 매 프레임 넘긴다 */
  setViewDir(x: number, y: number, z: number): void {
    this.trail.setView(x, y, z);
    this.aura.setView(x, z);
    this.streak.setView(x, y, z);
  }

  /** 프리셋 하나를 delay초 뒤에 띄운다(0이면 바로) */
  play(kind: ModuleKind, preset: FxPreset, x: number, y: number, z: number, yaw: number, delay = 0, lag = 0): boolean {
    const m = this.byKind[kind];
    if (delay <= 0) {
      const p = fillParams(this.params, preset, x, y, z, yaw);
      p.age0 = lag + (preset.age0 ?? 0);
      return m.spawn(p);
    }
    if (this.pendingN >= PENDING_CAP) return false;
    const q = this.pending[this.pendingN++];
    q.t = delay - lag; q.module = m; q.preset = preset; q.x = x; q.y = y; q.z = z; q.yaw = yaw;
    return true;
  }

  /**
   * 오라를 대상에 붙여 띄운다(바로). 오라는 target.x/y/z를 따라다니고, target.active = false가 되면 0.2초 안에 사라진다.
   * preset의 y는 대상 기준 높이 오프셋이다. 가득 차면 가장 오래된 오라를 대체한다.
   */
  playAura(preset: FxPreset, target: FxTarget): boolean {
    const p = fillParams(this.params, preset, target.x, 0, target.z, target.yaw);
    p.age0 = preset.age0 ?? 0;
    this.aura.follow(target);
    const ok = this.aura.spawn(p);
    this.aura.follow(null);
    return ok;
  }

  /**
   * 유도 줄기를 대상(영웅)에게 날려 보낸다(바로). 줄기 머리는 매 프레임 target 위치(+ preset.length 높이)를 쫓는다.
   * target이 null이면 고정 끝점(yaw 방향 ahead m). 가득 차면 가장 오래된 줄기를 대체한다
   */
  playStreak(preset: FxPreset, x: number, y: number, z: number, target: FxTarget | null): boolean {
    const p = fillParams(this.params, preset, x, y, z, 0);
    p.age0 = preset.age0 ?? 0;
    this.streak.follow(target);
    const ok = this.streak.spawn(p);
    this.streak.follow(null);
    return ok;
  }

  /** 큐 항목의 모든 층을 띄운다. 이미 지난 지연이 있으면 따라잡는다 */
  playEntry(entry: CueEntry, x: number, y: number, z: number, yaw: number, lag = 0): void {
    for (let i = 0; i < entry.layers.length; i++) {
      const l = entry.layers[i];
      this.play(l.module, l.preset, x, y, z, yaw, l.delay, lag);
    }
  }

  /**
   * 큐 이름으로 띄운다. live가 아닌 항목은 기록뿐이라 건너뛴다(force = 프리뷰·QA에서 재생).
   * 반환: 새 모듈이 그렸으면 true
   */
  playCue(name: string, x: number, y: number, z: number, yaw: number, lag = 0, force = false): boolean {
    this.cues.seen++;
    const e = findCue(name);
    if (e === undefined) { this.cues.unknown++; return false; }
    if (!e.live && !force) { this.cues.skipped++; return false; }
    this.cues.drawn++;
    this.playEntry(e, x, y, z, yaw, lag);
    return true;
  }

  /** Ev.SkillCue 한 건(a = 스킬 번호, b = 채널, c = 큐 번호, d = facing). vfx 채널만 처리한다 */
  onSkillCue(skillId: number, channel: number, index: number, x: number, y: number, z: number, facing: number, lag = 0, job: 0 | 1 = 0): boolean {
    if (channel !== 0) return false;
    const name = cueNameOf(skillId, index, 'kael', job);
    if (name === '') return false;
    // C9: 같은 이름이 여러 번 나오는 큐(공중 난무·대지 가르기 균열)는 n번째마다 앞으로/방향을 옮긴다
    const step = findCue(name)?.step;
    if (step !== undefined) {
      const n = cueOccurrenceOf(skillId, index, 'kael', job);
      if (step.ahead !== undefined) { x += Math.sin(facing) * step.ahead * n; z += Math.cos(facing) * step.ahead * n; }
      if (step.yaw !== undefined) facing += step.yaw * n;
    }
    return this.playCue(name, x, y, z, facing, lag);
  }

  /**
   * 스킬이 끊겼다(회피·피격·장면 전환): 그 스킬 큐의 cutOnCancel 모듈을 그 자리에서 비운다(아직 안 뜬 지연 층 포함).
   * 렌더가 CombatFx.onSkillCut으로 부른다. 반환: 지운 모듈 종류 수(QA)
   */
  cutSkill(skillId: number): number {
    let n = 0;
    for (let i = 0; i < CUE_MAP.length; i++) {
      const e = CUE_MAP[i];
      if (e.skillId !== skillId || e.cutOnCancel === undefined) continue;
      for (let k = 0; k < e.cutOnCancel.length; k++) {
        const m = this.byKind[e.cutOnCancel[k]];
        m.clear();
        n++;
        let j = 0;
        while (j < this.pendingN) {
          const q = this.pending[j];
          if (q.module === m) {
            q.module = null; q.preset = null;
            const last = --this.pendingN;
            if (j !== last) { this.pending[j] = this.pending[last]; this.pending[last] = q; }
            continue;
          }
          j++;
        }
      }
    }
    if (n > 0) this.cuts++;
    return n;
  }

  /** 스킬 끊김으로 모듈을 비운 횟수(QA) */
  cuts = 0;

  /** dtSec초(영웅 시계) 진행. 지연 큐를 처리하고 모듈을 갱신한다 */
  update(dtSec: number): void {
    let i = 0;
    while (i < this.pendingN) {
      const q = this.pending[i];
      q.t -= dtSec;
      if (q.t <= 0) {
        const p = fillParams(this.params, q.preset as FxPreset, q.x, q.y, q.z, q.yaw);
        p.age0 = -q.t + (q.preset?.age0 ?? 0);
        (q.module as FxModule).spawn(p);
        q.module = null; q.preset = null;
        const j = --this.pendingN;
        if (i !== j) { this.pending[i] = this.pending[j]; this.pending[j] = q; }
        continue;
      }
      i++;
    }
    for (let k = 0; k < this.all.length; k++) this.all[k].update(dtSec);
  }

  /** 틱 단위 진행(렌더 시계 heroDelta) */
  updateTicks(dtTicks: number): void { this.update(dtTicks / TICK_HZ); }

  clear(): void {
    for (const m of this.all) m.clear();
    for (const q of this.pending) { q.module = null; q.preset = null; }
    this.pendingN = 0;
  }

  prewarm(on: boolean): void {
    for (const m of this.all) m.prewarm(on);
  }

  dispose(): void {
    for (const m of this.all) m.dispose();
    this.noise.dispose();
    this.blastSheet.dispose();
    this.pendingN = 0;
  }
}
